import { Sparkline } from './Sparkline'

interface TrendCardProps {
  values: (number | null)[]
  /** Bucket labels (date strings), index-aligned with `values`. */
  buckets: string[]
  /** 'day' | 'week' | 'month' — drives label formatting + the caption. */
  bucketSize: string
  accent: string
  /** Section heading; defaults to "Trend" (mandal Overview labels each lens, e.g. "Coverage trend"). */
  title?: string
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const BUCKET_CAPTION: Record<string, string> = { day: 'Daily', week: 'Weekly', month: 'Monthly' }

/** "2026-05-18" → "May 18" (or "May" for month buckets). Parsed by hand to avoid locale/timezone drift. */
function fmtBucket(iso: string, bucketSize: string): string {
  const [, m, d] = iso.split('-')
  const mon = MONTHS_SHORT[Number(m) - 1] ?? ''
  return bucketSize === 'month' ? mon : `${mon} ${Number(d)}`
}

/** Up to ~7 evenly-spaced tick labels, always including the last bucket. */
function tickLabels(buckets: string[], bucketSize: string): string[] {
  if (buckets.length === 0) {
    return []
  }
  const max = 7
  const step = buckets.length <= max ? 1 : Math.ceil(buckets.length / max)
  const picked = buckets.filter((_, i) => i % step === 0)
  const last = buckets[buckets.length - 1]!
  if (picked[picked.length - 1] !== last) {
    picked.push(last)
  }
  return picked.map((b) => fmtBucket(b, bucketSize))
}

export function TrendCard({ values, buckets, bucketSize, accent, title = 'Trend' }: TrendCardProps) {
  const ticks = tickLabels(buckets, bucketSize)
  const caption = BUCKET_CAPTION[bucketSize] ?? 'Trend'
  const empty = buckets.length === 0 || values.every((v) => v === null)
  return (
    <>
      <div className="text-muted-text mx-0.5 mt-4 mb-2 flex items-center justify-between text-[10px] font-extrabold tracking-wider uppercase">
        {title} <span className="font-medium normal-case italic">{caption}</span>
      </div>
      <div className="rounded-[14px] border border-slate-900/10 bg-white px-3 py-1.5">
        {empty ? (
          <div className="text-muted-text py-6 text-center text-xs">No trend data for this period</div>
        ) : (
          <>
            <Sparkline values={values} accent={accent} />
            <div className="text-muted-text flex justify-between px-1 pt-0.5 pb-1.5 text-[9px]">
              {ticks.map((t, i) => (
                <span key={`${t}-${i}`}>{t}</span>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  )
}
